import { CloseOutlined } from '@ant-design/icons';
import { motion } from 'motion/react';
import { CSSProperties, useEffect } from 'react';
import { createPortal } from 'react-dom';

import CliSessionChooser from './CliSessionChooser';
import CliToolKeyPane from './CliToolKeyPane';
import { setCliPicking } from './cliSessionStore';
import { useSurfaceLift } from 'utils/surfaceStack';

interface CliSessionPickerProps {
  sessions: string[];
  authFor: string | null;
}

const PANEL_MOTION = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 38,
  mass: 0.8,
};

function CliSessionPicker({
  sessions,
  authFor,
}: Readonly<CliSessionPickerProps>) {
  const { lift, raise } = useSurfaceLift('cli-picker');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCliPicking(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const panel = (
    <motion.div
      className="CliSessionPicker__scrim"
      style={{ '--surface-lift': lift } as CSSProperties}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      onPointerDownCapture={raise}
      onClick={() => setCliPicking(false)}
    >
      <motion.section
        className="CliSessionPicker"
        role="dialog"
        aria-label="Open a CLI session"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={PANEL_MOTION}
        onClick={event => event.stopPropagation()}
      >
        <header className="CliSessionPicker__header">
          <span className="CliSessionPicker__title">Open a CLI session</span>
          <button
            type="button"
            className="CliSessionPicker__close"
            aria-label="Close"
            onClick={() => setCliPicking(false)}
          >
            <CloseOutlined />
          </button>
        </header>

        <p className="CliSessionPicker__lede">
          A session becomes a tab in the dock and keeps running while you
          browse. Support cannot generate a token: the cluster&apos;s owner
          generates it in the Cloud Portal and hands it over.
        </p>

        <div className="CliSessionPicker__layout">
          <CliSessionChooser sessions={sessions} authFor={authFor} />
          <aside className="CliSessionPicker__aside">
            <CliToolKeyPane compact />
          </aside>
        </div>
      </motion.section>
    </motion.div>
  );

  return createPortal(panel, document.body);
}

export default CliSessionPicker;
